/**
 * Question Parser Utility
 *
 * Parses embedded questions from markdown content
 * Supports formats:
 * - ```question:mcq ... ```
 * - ```question:short ... ```
 * - ```question:code ... ```
 */

/**
 * Helper to finalize a section and add content to question object
 */
const finalizeSection = (question, section, content) => {
  const text = content.join('\n').trim();

  if (section === 'question') {
    question.question = text;
  } else if (section === 'options') {
    // Parse options: "A: Text here"
    const options = [];
    for (const line of content) {
      const match = line.trim().match(/^([A-Z]):\s*(.+)$/);
      if (match) {
        options.push({
          key: match[1],
          text: match[2].trim()
        });
      }
    }
    question.options = options;
  } else if (section === 'rubric') {
    question.rubric = text;
  } else if (section === 'explanation') {
    question.explanation = text;
  }
};

/**
 * Parse MCQ question block
 * Expected format:
 * ```question:mcq
 * ID: Q1
 * TYPE: single  # or multiple
 * LO: Understanding of size operator
 * QUESTION:
 * What does `size(A, 1)` return in MATLAB?
 * OPTIONS:
 * A: Number of rows in A
 * B: Number of columns in A
 * C: Total number of elements in A
 * D: The size of A in bytes
 * RUBRIC: A
 * EXPLANATION:
 * `size(A,1)` gives the size of the first dimension, i.e., number of rows.
 * ```
 */
export const parseMCQ = (content) => {
  const lines = content.split('\n');
  const question = {
    questionType: 'mcq',
    id: null,
    type: 'single', // default
    lo: null,
    question: '',
    options: [],
    rubric: [],
    explanation: null
  };

  let currentSection = null;
  let currentContent = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith('#')) continue;

    // Check for section headers
    if (trimmed.startsWith('ID:')) {
      question.id = trimmed.substring(3).trim();
    } else if (trimmed.startsWith('TYPE:')) {
      const type = trimmed.substring(5).trim().split('#')[0].trim();
      question.type = type === 'multiple' ? 'multiple' : 'single';
    } else if (trimmed.startsWith('LO:')) {
      question.lo = trimmed.substring(3).trim();
    } else if (trimmed === 'QUESTION:') {
      if (currentSection) {
        finalizeSection(question, currentSection, currentContent);
      }
      currentSection = 'question';
      currentContent = [];
    } else if (trimmed === 'OPTIONS:') {
      if (currentSection) {
        finalizeSection(question, currentSection, currentContent);
      }
      currentSection = 'options';
      currentContent = [];
    } else if (trimmed.startsWith('RUBRIC:')) {
      if (currentSection) {
        finalizeSection(question, currentSection, currentContent);
      }
      const rubricText = trimmed.substring(7).trim();
      // Parse rubric - can be single letter or comma-separated (A,B)
      question.rubric = rubricText.split(',').map(r => r.trim());
      currentSection = null;
      currentContent = [];
    } else if (trimmed === 'EXPLANATION:') {
      if (currentSection) {
        finalizeSection(question, currentSection, currentContent);
      }
      currentSection = 'explanation';
      currentContent = [];
    } else {
      // Add to current section content
      if (currentSection) {
        currentContent.push(line);
      }
    }
  }

  // Finalize last section
  if (currentSection) {
    finalizeSection(question, currentSection, currentContent);
  }

  return question;
};

/**
 * Parse short answer question block
 * Expected format:
 * ```question:short
 * ID: Q2
 * LO: difference of size and length
 * QUESTION:
 * Explain the difference between `size` and `length` in MATLAB.
 * RUBRIC:
 * `size` returns size along each dimension; `length` returns the largest dimension.
 * ```
 */
export const parseShortAnswer = (content) => {
  const lines = content.split('\n');
  const question = {
    questionType: 'short',
    id: null,
    lo: null,
    question: '',
    rubric: null
  };

  let currentSection = null;
  let currentContent = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith('#')) continue;

    // Check for section headers
    if (trimmed.startsWith('ID:')) {
      question.id = trimmed.substring(3).trim();
    } else if (trimmed.startsWith('LO:')) {
      question.lo = trimmed.substring(3).trim();
    } else if (trimmed === 'QUESTION:') {
      if (currentSection) {
        finalizeSection(question, currentSection, currentContent);
      }
      currentSection = 'question';
      currentContent = [];
    } else if (trimmed === 'RUBRIC:') {
      if (currentSection) {
        finalizeSection(question, currentSection, currentContent);
      }
      currentSection = 'rubric';
      currentContent = [];
    } else {
      // Add to current section content
      if (currentSection) {
        currentContent.push(line);
      }
    }
  }

  // Finalize last section
  if (currentSection) {
    finalizeSection(question, currentSection, currentContent);
  }

  return question;
};

/**
 * Extract all question blocks from markdown content
 * Returns array of { type, content, startIndex, endIndex, parsed }
 */
export const extractQuestions = (markdown) => {
  const questions = [];
  const regex = /```question:(mcq|short|code)\n([\s\S]*?)```/g;

  let match;
  while ((match = regex.exec(markdown)) !== null) {
    const questionType = match[1];
    const content = match[2];
    const startIndex = match.index;
    const endIndex = match.index + match[0].length;

    let parsed = null;
    if (questionType === 'mcq') {
      parsed = parseMCQ(content);
    } else if (questionType === 'short') {
      parsed = parseShortAnswer(content);
    }

    questions.push({
      type: questionType,
      content,
      startIndex,
      endIndex,
      parsed
    });
  }

  return questions;
};

/**
 * Replace question blocks in markdown with placeholder tokens
 * Returns { contentWithPlaceholders, questionMap }
 */
export const replaceQuestionsWithPlaceholders = (markdown) => {
  const questions = extractQuestions(markdown);
  let content = markdown;
  const questionMap = new Map();

  // Replace in reverse order to maintain indices
  for (let i = questions.length - 1; i >= 0; i--) {
    const question = questions[i];
    // Use a code block with special language identifier, just like runnable code
    const placeholder = `\`\`\`question:${i}\n\`\`\``;

    content =
      content.substring(0, question.startIndex) +
      '\n\n' + placeholder + '\n\n' +
      content.substring(question.endIndex);

    questionMap.set(`question:${i}`, question);
  }

  return { contentWithPlaceholders: content, questionMap };
};

/**
 * Validate question format
 */
export const validateQuestion = (questionData) => {
  const errors = [];

  if (!questionData.id) {
    errors.push('Question ID is required');
  }

  if (!questionData.question || questionData.question.trim().length === 0) {
    errors.push('Question text is required');
  }

  if (questionData.questionType === 'mcq') {
    if (!questionData.options || questionData.options.length === 0) {
      errors.push('MCQ must have options');
    }
    if (!questionData.rubric || questionData.rubric.length === 0) {
      errors.push('MCQ must have a rubric (correct answer)');
    }
  }

  if (questionData.questionType === 'short') {
    if (!questionData.rubric) {
      errors.push('Short answer must have a rubric (expected answer)');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Generate question markdown from structured data
 */
export const generateQuestionMarkdown = (questionData) => {
  if (questionData.questionType === 'mcq') {
    let markdown = '```question:mcq\n';
    markdown += `ID: ${questionData.id}\n`;
    markdown += `TYPE: ${questionData.type}\n`;
    if (questionData.lo) {
      markdown += `LO: ${questionData.lo}\n`;
    }
    markdown += 'QUESTION:\n';
    markdown += `${questionData.question}\n`;
    markdown += 'OPTIONS:\n';
    questionData.options.forEach(opt => {
      markdown += `${opt.key}: ${opt.text}\n`;
    });
    markdown += `RUBRIC: ${questionData.rubric.join(', ')}\n`;
    if (questionData.explanation) {
      markdown += 'EXPLANATION:\n';
      markdown += `${questionData.explanation}\n`;
    }
    markdown += '```';
    return markdown;
  }

  if (questionData.questionType === 'short') {
    let markdown = '```question:short\n';
    markdown += `ID: ${questionData.id}\n`;
    if (questionData.lo) {
      markdown += `LO: ${questionData.lo}\n`;
    }
    markdown += 'QUESTION:\n';
    markdown += `${questionData.question}\n`;
    markdown += 'RUBRIC:\n';
    markdown += `${questionData.rubric}\n`;
    markdown += '```';
    return markdown;
  }

  return '';
};
